import classNames from "classnames"
import GridTableToggle, { IndexType } from "components/Extra/GridTableToggle"
import React, { useEffect, useState } from "react"
import { Form } from "react-router-dom"
import { Button, Input, NavLink } from "reactstrap"
import i18n from "../../i18n"

function filterTableRows<T extends Record<string, any>>(
  data: T[],
  searchText: string
): T[] {
  if (!searchText.trim()) return data

  const lowerSearch = searchText.toLowerCase()

  return data.filter(row =>
    Object.values(row).some(value =>
      String(value).toLowerCase().includes(lowerSearch)
    )
  )
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

interface FilterDataProps<T = Record<string, any>> {
  data: T[]
  bearerFilteredData: (filteredData: T[]) => void
  activeTab?: IndexType
  setActiveTab?: (tab: IndexType) => void
  showSearchFilterBtn?: boolean
  showFilterBtnInsteadOfToggle?: boolean
}

export const FilterData = <T extends Record<string, any>>({
  data,
  bearerFilteredData,
  activeTab,
  setActiveTab,
  showSearchFilterBtn = false,
  showFilterBtnInsteadOfToggle = false,
}: FilterDataProps<T>) => {
  const [value, setValue] = useState("")
  const debouncedValue = useDebounce(value, 300)

  useEffect(() => {
    const trimmedValue = debouncedValue.trim()
    if (!trimmedValue.length) {
      bearerFilteredData(data)
      return
    }
    bearerFilteredData(filterTableRows(data, trimmedValue))
  }, [debouncedValue, data, bearerFilteredData])

  return (
    <React.Fragment>
      <div className="mt-4 mt-sm-0 float-sm-end d-sm-flex align-items-center justify-content-between w-100 mb-3">
        <div className="search-box me-2 flex-1 d-flex align-items-center gap-3 flex-grow-1 w-100">
          <div className="product-view-nav d-flex justify-content-start align-items-center gap-0.5 w-100">
            <div className="position-relative w-100">
              <Input
                type="text"
                className="form-control border-1 border-light rounded-0 bg-lightBlue rounded-3"
                style={{ height: "46px" }}
                placeholder={i18n.t("common_search") as string}
                onChange={e => setValue(e.target.value)}
                value={value || ""}
              />
              <i className="bx bx-search-alt search-icon mt-1" />
            </div>
            {showSearchFilterBtn && (
              <NavLink
                className={classNames(
                  {
                    active: true,
                  },
                  "bg-primary"
                )}
                onClick={() => {
                  console.log("Clicked index icon")
                }}
                style={{
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "15px",
                  margin: "2px",
                }}
              >
                <i className="fas fa-sliders-h" />
              </NavLink>
            )}
          </div>
        </div>
        {showFilterBtnInsteadOfToggle ? (
          <Button
            onClick={() => setValue("")}
            color="success"
            className="bg-gradiant-blue text-capitalize border-0 rounded-3"
            style={{ minWidth: "fit-content", height: "46px" }}
          >
            {i18n.t("common_viewAll")}
          </Button>
        ) : (
          activeTab &&
          setActiveTab && (
            <GridTableToggle
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          )
        )}
      </div>
    </React.Fragment>
  )
}

export default FilterData
